/**
 * CopilotDrawer.
 *
 * The floating-button-plus-overlay that lives on every authenticated
 * page (rendered in AppLayout). The button sits bottom-right; clicking
 * opens a full overlay with the conversation list / picker / active chat.
 *
 * It was a 400x600 panel in the corner, which was the wrong size for the
 * work it does. The copilot answers with tables of cases, org snapshots
 * and multi-section analyses - a caseload comes back as "Case ID | Status
 * | Opened On | Days Open", and four columns in a 400px box is a table
 * nobody can read. The panel was sized for a support-chat widget while
 * the thing inside it was doing analysis.
 *
 * So: a real overlay. Near-full-screen on a phone, a wide centred sheet
 * on a desktop, with the reading column capped so prose does not run to
 * 200 characters just because the window is wide.
 *
 * State machine:
 *   closed - only the floating button is visible
 *   open + no convo - drawer shows PlaybookLauncher
 *   open + active convo - drawer shows ConversationView
 *   open + list view - drawer shows ConversationList
 *
 * Persistence:
 *   - The "current conversation id" lives in localStorage so it
 *     survives refreshes (Andy's pattern: people don't lose context).
 *   - Drawer open/closed state is NOT persisted - every page load
 *     starts collapsed so the chrome doesn't get in the way.
 */
import { Suspense, lazy, useEffect, useState } from "react";

// BotMessageSquare rather than a bare speech bubble: the button does not open
// a chat with a person, and a plain bubble said it did. Not Sparkles either -
// sparkles is the reflex mark for "AI happens here" and says nothing about
// what this one is. This is an agent you talk to, which is the icon.
import { BotMessageSquare, Plus, X } from "lucide-react";

import { useOnboardingMark } from "@/features/onboarding/model/useOnboardingMark";

import {
  useArchiveConversationMutation,
  useCreateConversationMutation,
  useListConversationsQuery,
} from "../api/copilotApi";

// Lazy-load the heavier inner views. The floating button + drawer chrome
// stay in the main bundle (small), but the conversation rendering and
// playbook launcher only fetch when the user opens the drawer. Each gets
// its own JS chunk via Vite.
const ConversationView = lazy(() =>
  import("./ConversationView").then((m) => ({ default: m.ConversationView })),
);
const PlaybookLauncher = lazy(() =>
  import("./PlaybookLauncher").then((m) => ({ default: m.PlaybookLauncher })),
);

import { cn } from "@/shared/lib/cn";

import { plainPreview } from "../lib/plainPreview";
import { sanitizeStubText } from "../lib/sanitizeStubText";

import { CopilotComposer } from "./CopilotComposer";
import { CopilotGreeting } from "./CopilotGreeting";

// localStorage key for the last-open conversation id. Exported so the
// authCacheListener can clear it on logout/login - otherwise a stale id
// from a previous user persists on a shared browser (tenant-isolation
// audit V2). Backend scoping means a stale id only yields a 404, never
// another user's data, but the drawer should not open to a stranger's
// conversation id.
/** Storage key for the copilot's active conversation id. */
export const COPILOT_ACTIVE_CONVERSATION_KEY =
  "tnn::copilot::active_conversation_id";
const STORAGE_KEY = COPILOT_ACTIVE_CONVERSATION_KEY;

type View = "list" | "launcher" | "conversation";

/** Floating-button + slide-out copilot panel, present on every authenticated page. */
export function CopilotDrawer() {
  // Local state: open
  const [open, setOpen] = useState(false);
  // The first question, held here rather than inside the greeting: the field
  // it belongs to is the drawer's footer, not part of the scrolling content.
  const [draft, setDraft] = useState("");
  const markOnboarding = useOnboardingMark();
  // Mark the "copilot" onboarding step the first time the drawer opens.
  // useEffect on `open` so we only fire when it flips true.
  useEffect(() => {
    if (open) markOnboarding("copilot");
  }, [open, markOnboarding]);

  // Allow other components (e.g. the onboarding welcome modal's
  // "Ask the AI Copilot" self-help link) to open the drawer via a
  // custom DOM event. Deliberately a DOM event rather than a global
  // redux slice - minimal coupling. The event name is intentionally
  // namespaced so it's greppable as a public API.
  useEffect(() => {
    // Event handler: handler
    const handler = () => setOpen(true);
    window.addEventListener("tnn:open-copilot", handler);
    return () => window.removeEventListener("tnn:open-copilot", handler);
  }, []);
  // Local state: activeId
  const [activeId, setActiveId] = useState<number | null>(() => {
    if (typeof window === "undefined") return null;
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? Number(raw) : null;
  });
  // Local state: view
  const [view, setView] = useState<View>(activeId ? "conversation" : "list");

  const { data: convoList } = useListConversationsQuery(undefined, { skip: !open });
  const [archiveConversation] = useArchiveConversationMutation();
  const [createConversation] = useCreateConversationMutation();

  // Escape closes it. The overlay declares role="dialog" aria-modal="true",
  // and a dialog that cannot be dismissed from the keyboard is a promise the
  // markup makes and the behaviour breaks. It also covers most of the page
  // now, so the reflex to press Escape is the right one.
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open]);

  // Nothing behind the overlay should scroll while it is open: a phone sheet
  // that scrolls the page underneath is the classic way to lose your place.
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  // Persist the active conversation id
  useEffect(() => {
    if (activeId === null) {
      localStorage.removeItem(STORAGE_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, String(activeId));
    }
  }, [activeId]);

  const openConversation = (id: number) => {
    setActiveId(id);
    setView("conversation");
  };

  const goToLauncher = () => {
    setActiveId(null);
    setView("launcher");
  };

  const goToList = () => {
    setActiveId(null);
    setView("list");
  };

  const handleStartChatFromGreeting = async (prompt: string) => {
    try {
      const convo = await createConversation({}).unwrap();
      sessionStorage.setItem("tnn::copilot::initial_prompt", prompt);
      openConversation(convo.id);
    } catch {
      // User can still start via PlaybookLauncher or free chat
    }
  };

  // Event handler: handleArchive
  const handleArchive = async () => {
    if (!activeId) return;
    try {
      await archiveConversation({ id: activeId }).unwrap();
    } finally {
      goToList();
    }
  };

  return (
    <>
      {/* Floating button - always visible when authenticated */}
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? "Close copilot" : "Open copilot"}
        className={cn(
          "fixed bottom-5 right-5 z-40 flex h-12 w-12 items-center justify-center rounded-full shadow-lg transition-transform",
          "bg-primary text-primary-foreground hover:scale-105 active:scale-95",
          open && "rotate-90",
        )}
      >
        {open ? <X size={20} /> : <BotMessageSquare size={20} />}
      </button>

      {/* Backdrop. Now on every size, not just mobile: the overlay covers
          enough of the page that clicking away is the natural exit, and a
          dimmed page is what says "this is on top of your work". */}
      {open && (
        <div
          onClick={() => setOpen(false)}
          aria-hidden
          className="tnn-overlay fixed inset-0 z-30 bg-black/40 backdrop-blur-[2px] motion-safe:animate-fade-in sm:left-[240px]"
        />
      )}

      {/* Overlay */}
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Copilot"
        className={cn(
          "fixed z-30 flex flex-col overflow-hidden border border-border bg-background shadow-2xl",
          "motion-safe:transition-all motion-safe:duration-200",
          // Phone: a sheet that leaves the top of the page visible, so it
          // is obvious what it is sitting on. The sidebar is off-canvas at
          // this size, so full width is free.
          "inset-x-0 bottom-0 top-16 rounded-t-2xl",
          // Desktop: inside the content area, clear of the 240px sidebar.
          // The sidebar is z-50 and this is z-30, so anything underneath it
          // is not merely tight - it is painted over. Starting right of the
          // nav also leaves it usable while the copilot is open, which is
          // the point of an overlay that is not a modal takeover.
          "sm:left-[calc(240px+1.5rem)] sm:right-6 sm:top-8 sm:bottom-8",
          "sm:mx-auto sm:max-w-4xl sm:rounded-2xl",
          open
            ? "pointer-events-auto translate-y-0 opacity-100"
            : "pointer-events-none translate-y-2 opacity-0",
        )}
      >
        {/* Header. Sized for the overlay rather than the old corner panel:
            at this width 10px chrome reads as an oversight, not restraint. */}
        <div className="flex shrink-0 items-center justify-between gap-3 border-b border-border px-4 py-3 sm:px-6 sm:py-4">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground">Copilot</p>
            <p className="truncate text-xs text-muted-foreground">
              {view === "list"
                ? "Your conversations"
                : view === "launcher"
                  ? "Ask a question, or start a workflow"
                  : "Working with your organisation's data"}
            </p>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            {view === "list" ? (
              <button
                type="button"
                onClick={goToLauncher}
                className="flex items-center gap-1.5 rounded-md border border-border bg-background px-2.5 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-muted/60 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
              >
                <Plus size={14} aria-hidden />
                New conversation
              </button>
            ) : (
              <button
                type="button"
                onClick={goToList}
                className="rounded-md px-2.5 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
              >
                All conversations
              </button>
            )}
            {/* Closing from inside the overlay: the floating button is
                behind it on a phone, and reaching past a sheet to dismiss
                it is not a gesture anybody should have to find. */}
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close copilot"
              className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
            >
              <X size={16} aria-hidden />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          {view === "list" && (
            <ConversationList
              conversations={convoList?.results ?? []}
              onPick={openConversation}
              onNew={goToLauncher}
            />
          )}
          {view === "launcher" && (
            <Suspense fallback={<DrawerFallback />}>
              {/* One scroll region, one measure, one set of padding. This
                  used to stack two self-contained screens - each with its
                  own heading, its own padding and its own idea of the type
                  scale - so the reader met "I can help with" and "How can I
                  help?" one after the other, and the workflows sat below the
                  fold of a nested scroller. */}
              {/* Scroll the suggestions, pin the composer. The thing you came
                  here to do does not scroll away from you. */}
              <div className="flex h-full flex-col">
                <div className="flex-1 overflow-y-auto px-4 py-5 sm:px-6">
                  <div className="mx-auto w-full max-w-3xl space-y-8">
                    <CopilotGreeting onStartChat={handleStartChatFromGreeting} />
                    <PlaybookLauncher onConversationStarted={openConversation} />
                  </div>
                </div>
                <div className="shrink-0 border-t border-border bg-card px-4 py-3 sm:px-6">
                  <CopilotComposer
                    className="mx-auto w-full max-w-3xl"
                    value={draft}
                    onChange={setDraft}
                    onSubmit={() => {
                      const text = draft.trim();
                      if (!text) return;
                      setDraft("");
                      void handleStartChatFromGreeting(text);
                    }}
                    placeholder="Ask anything about your organisation…"
                    submitLabel="Ask"
                    hint="Enter to send, Shift+Enter for a new line"
                  />
                </div>
              </div>
            </Suspense>
          )}
          {view === "conversation" && activeId !== null && (
            <Suspense fallback={<DrawerFallback />}>
              <ConversationView
                conversationId={activeId}
                onArchive={handleArchive}
              />
            </Suspense>
          )}
        </div>
      </aside>
    </>
  );
}

function DrawerFallback() {
  return (
    <div className="flex h-full items-center justify-center text-xs text-muted-foreground">
      Loading…
    </div>
  );
}

// -----------------------------------------------------------------------------
// Conversation list
// -----------------------------------------------------------------------------

function ConversationList({
  conversations,
  onPick,
  onNew,
}: {
  conversations: Array<{
    id: number;
    title: string;
    status: string;
    last_activity_at: string;
    last_message_preview: string;
    message_count: number;
  }>;
  onPick: (id: number) => void;
  onNew: () => void;
}) {
  if (conversations.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center px-6 text-center">
        <BotMessageSquare size={32} className="mb-3 text-muted-foreground/40" />
        <p className="text-sm font-medium text-foreground">
          No conversations yet
        </p>
        <p className="mt-1 text-2xs text-muted-foreground">
          Start a playbook or just chat with the copilot.
        </p>
        <button
          onClick={onNew}
          className="mt-4 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90"
        >
          Start a conversation
        </button>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto">
      {conversations.map((c) => (
        <button
          key={c.id}
          onClick={() => onPick(c.id)}
          className="block w-full border-b border-border px-4 py-3 text-left hover:bg-muted/30"
        >
          <div className="flex items-start justify-between gap-2">
            <p className="truncate text-sm font-medium text-foreground">
              {c.title || "Untitled"}
            </p>
            <span className="shrink-0 text-3xs text-muted-foreground">
              {formatRelative(c.last_activity_at)}
            </span>
          </div>
          <p className="mt-0.5 line-clamp-2 text-2xs text-muted-foreground">
            {c.last_message_preview
              ? plainPreview(sanitizeStubText(c.last_message_preview))
              : "No messages yet"}
          </p>
          {c.status !== "active" && (
            <span className="mt-1 inline-flex rounded-full bg-muted px-1.5 py-0.5 text-3xs uppercase tracking-wide text-muted-foreground">
              {c.status}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = now - then;
  const min = Math.floor(diff / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const days = Math.floor(hr / 24);
  if (days < 7) return `${days}d ago`;
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  } catch {
    return "";
  }
}
